import { environment } from "../config/environment";
import { customFetch } from "./Auth";

const BASE = `${environment.apiUrl}/evaluation`;

// POST /api/evaluation/datasets/generate
// payload: { lambdaName, datasetName, patientIds: string[], createdBy? }
// Triggers the admin-dashboard server to query the configured summarize-jobs table
// for each patient_id, build a wide CSV, and upload it to Hugging Face as
// `Knowtex-ai/{lambdaName}__{datasetName}`.
export const generateEvalDataset = async (payload) => {
  try {
    const res = await customFetch(`${BASE}/datasets/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        token: localStorage.token,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          errorData.detail ||
          `Eval dataset generation failed with status: ${res.status}`,
      );
    }

    return await res.json();
  } catch (error) {
    console.error("Error in generateEvalDataset:", error);
    throw error;
  }
};

// DELETE /api/evaluation/datasets/:name
// Deletes the Hugging Face dataset repo for an eval dataset (name without
// the `Knowtex-ai/` prefix, e.g. "RunMDMFunction__foo").
export const deleteEvalDataset = async (name) => {
  try {
    const res = await customFetch(
      `${BASE}/datasets/${encodeURIComponent(name)}`,
      {
        method: "DELETE",
        headers: { token: localStorage.token },
      },
    );
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to delete dataset (${res.status})`,
      );
    }
    return await res.json();
  } catch (error) {
    console.error("Error in deleteEvalDataset:", error);
    throw error;
  }
};

// POST /api/evaluation/datasets/:name/run
// payload: { evalConfig: { model: string } }
// Queues one SQS message per dataset row to the ChatgptSummarize queue with
// `is_eval: true` so RunChatgptFunction runs in eval mode (writes only to
// Eval-SummarizeJobs-{env}, no Postgres / SNS / downstream SQS side-effects).
export const runEval = async (name, evalConfig) => {
  try {
    const res = await customFetch(
      `${BASE}/datasets/${encodeURIComponent(name)}/run`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: localStorage.token,
        },
        body: JSON.stringify({ evalConfig }),
      },
    );
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to start eval run (${res.status})`,
      );
    }
    return await res.json();
  } catch (error) {
    console.error("Error in runEval:", error);
    throw error;
  }
};

// GET /api/evaluation/datasets
// Lists evaluation datasets directly from Hugging Face (Knowtex-ai org),
// filtered to those with a `{lambda}__` prefix.
export const listEvalDatasets = async () => {
  try {
    const res = await customFetch(`${BASE}/datasets`, {
      method: "GET",
      headers: { token: localStorage.token },
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(
        errorData.error || `Failed to list eval datasets (${res.status})`,
      );
    }
    const data = await res.json();
    return Array.isArray(data?.datasets) ? data.datasets : [];
  } catch (error) {
    console.error("Error in listEvalDatasets:", error);
    throw error;
  }
};
